import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { useBankNotesAndCoins } from '../euro';
import { useCashListConfiguration } from '@pflegenavi/frontend/api-nursing-home';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import type { CashListIconComponent } from '@pflegenavi/web-components';
import {
  getCashListIcon,
  MoneyInput,
  Stack,
  Typography,
} from '@pflegenavi/web-components';
import { MAX_BANK_ACCOUNT_CHANGE } from '@pflegenavi/shared/constants';
import { AdjustProceedRow } from './BankAccountForm';
import { ChangeSummaryCard } from './cards/ChangeSummaryCard';
import { ChangeCashMode } from './model';
import { CashListStorageType } from '@pflegenavi/shared/api';

interface BankAccountAdjustFormProps {
  bankAccountChange?: number;
  setBankAccountChange: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  handleClose: () => void;
  nextStep?: () => void;
  cashListId: string;
  storageType: CashListStorageType;
  showMainCashboxField?: boolean;
  mainCashbox?: boolean;
  setMainCashbox?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
}

export const BankAccountAdjustForm: FC<BankAccountAdjustFormProps> = ({
  bankAccountChange,
  setBankAccountChange,
  nextStep,
  handleClose,
  cashListId,
  storageType,
  showMainCashboxField,
  mainCashbox,
  setMainCashbox,
}) => {
  const {
    bankAccountAmount: totalBankBalance,
    isLoading: totalBankBalanceLoading,
  } = useBankNotesAndCoins(cashListId);
  const cashListConfiguration = useCashListConfiguration();
  const cashList = cashListConfiguration.cashLists.find(
    (cl) => cl.id === cashListId
  );

  if (totalBankBalance === undefined || totalBankBalanceLoading) {
    return null;
  }

  return (
    <BankAccountAdjustFormView
      totalBankBalance={totalBankBalance}
      setBankAccountChange={setBankAccountChange}
      bankAccountChange={bankAccountChange}
      nextStep={nextStep}
      handleClose={handleClose}
      storageType={storageType}
      showMainCashboxField={showMainCashboxField}
      mainCashbox={mainCashbox}
      setMainCashbox={setMainCashbox}
      cashListName={cashList?.name}
      cashListIcon={
        cashList && getCashListIcon(cashList, cashListConfiguration)
      }
    />
  );
};

interface BankAccountAdjustFormViewProps {
  totalBankBalance: number;
  setBankAccountChange: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  bankAccountChange?: number;
  handleClose: () => void;
  nextStep?: () => void;
  storageType: CashListStorageType;
  showMainCashboxField?: boolean;
  mainCashbox?: boolean;
  setMainCashbox?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  cashListName?: string;
  cashListIcon?: CashListIconComponent;
}

export const BankAccountAdjustFormView: FC<BankAccountAdjustFormViewProps> = ({
  totalBankBalance,
  setBankAccountChange,
  bankAccountChange,
  handleClose,
  nextStep,
  storageType,
  showMainCashboxField,
  mainCashbox,
  setMainCashbox,
  cashListName,
  cashListIcon,
}) => {
  const { t } = useTranslation();
  const { fNumber } = useFormatting();

  const change = bankAccountChange ?? 0;
  const newBankAccountAmount = totalBankBalance + change;
  const newBankAccountAmountNegative = change < 0 && newBankAccountAmount < 0;

  const bankOnly = storageType === CashListStorageType.BankAccount;

  const mainCashboxRadios = showMainCashboxField && setMainCashbox && (
    <RadioGroup
      value={mainCashbox === undefined ? '' : String(mainCashbox)}
      onChange={(_, value) => setMainCashbox(value === 'true')}
    >
      <FormControlLabel
        value="true"
        control={<Radio data-cy="main-cashbox-from-main" />}
        label={t('cashManagement.main-cashbox.from-main')}
      />
      <FormControlLabel
        value="false"
        control={<Radio data-cy="main-cashbox-other" />}
        label={t('cashManagement.main-cashbox.other')}
      />
    </RadioGroup>
  );

  return (
    <>
      {storageType & CashListStorageType.BankAccount ? (
        <Stack gap={2}>
          <Typography fontWeight={700}>
            {bankOnly
              ? t('cashManagement.account.adjustment')
              : t('cashManagement.total-account')}
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={3}
          >
            <Stack gap={2} sx={{ flex: 1 }}>
              <MoneyInput
                className="bankAccountAdjustInput"
                sx={{ alignSelf: 'flex-start' }}
                allowNegative
                label={
                  bankOnly
                    ? t('cashManagement.account.adjustment-amount')
                    : t('common.amount')
                }
                placeholder={`±${fNumber(0, 2)}`}
                value={bankAccountChange}
                maxValue={MAX_BANK_ACCOUNT_CHANGE}
                onChange={setBankAccountChange}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
              {mainCashboxRadios}
            </Stack>
            <Stack gap={1}>
              {cashListName && cashListIcon && (
                <ChangeSummaryCard
                  mode={ChangeCashMode.Adjust}
                  cashListName={cashListName}
                  Icon={cashListIcon}
                  currentBalance={totalBankBalance}
                  amount={change}
                  hideHeader
                />
              )}
              {newBankAccountAmountNegative && (
                <Typography variant="caption" color="error" textAlign="right">
                  {t('cashManagement.bank-account-new-balance-negative-error')}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      ) : (
        mainCashboxRadios
      )}
      <AdjustProceedRow
        nextStep={newBankAccountAmountNegative ? undefined : nextStep}
        handleClose={handleClose}
      />
    </>
  );
};
